import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useProfiles } from '@/api/profiles'
import { useExperimentMode, useUpdateExperimentMode } from '@/api/experiments'
import { Link2, RefreshCw, CheckCircle2, XCircle, AlertCircle, FlaskConical } from 'lucide-react'

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const queryClient = useQueryClient()

  // LWA Callback-Parameter auswerten
  useEffect(() => {
    const lwaSuccess = searchParams.get('lwa_success')
    const lwaError = searchParams.get('lwa_error')
    const profileCount = searchParams.get('profiles')

    if (lwaSuccess) {
      setNotification({
        type: 'success',
        message: `Amazon-Account erfolgreich verknuepft. ${profileCount} Profile geladen.`,
      })
      queryClient.invalidateQueries({ queryKey: ['lwa-profiles'] })
      setSearchParams({}, { replace: true })
    } else if (lwaError) {
      setNotification({
        type: 'error',
        message: `Amazon-Verbindung fehlgeschlagen: ${lwaError}`,
      })
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams, queryClient])

  const { data: profiles, isLoading } = useProfiles()

  const refreshMutation = useMutation({
    mutationFn: (profileId: number) => api.post('/lwa/refresh', { profileId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lwa-profiles'] })
    },
  })

  async function handleConnectAmazon() {
    // Authorize-URL per Bearer-Auth holen (Browser-Redirect kann keinen Header mitschicken),
    // dann redirecten. Backend-Route /lwa/authorize-url prueft die Session und baut state+url.
    try {
      const { authorizeUrl } = await api.get<{ authorizeUrl: string }>('/lwa/authorize-url')
      window.location.href = authorizeUrl
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setNotification({ type: 'error', message: `OAuth-Start fehlgeschlagen: ${msg}` })
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Einstellungen</h1>
        <p className="text-sm text-[var(--muted-foreground)]">
          Amazon-Account und API-Verbindung verwalten
        </p>
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`flex items-center gap-2 rounded-md border p-4 text-sm ${
            notification.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300'
              : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300'
          }`}
        >
          {notification.type === 'success' ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          {notification.message}
          <button
            onClick={() => setNotification(null)}
            className="ml-auto text-xs underline"
          >
            Schliessen
          </button>
        </div>
      )}

      {/* Amazon Account Connection */}
      <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Amazon Advertising API</h2>
            <p className="text-sm text-[var(--muted-foreground)]">
              Verknuepfe deinen Amazon-Account fuer Kampagnenverwaltung
            </p>
          </div>
          <button
            onClick={handleConnectAmazon}
            className="flex items-center gap-2 rounded-md bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600"
          >
            <Link2 className="h-4 w-4" />
            {profiles && profiles.length > 0 ? 'Neu verbinden' : 'Amazon verbinden'}
          </button>
        </div>
      </div>

      {/* Advertising Profiles */}
      <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-6">
        <h2 className="mb-4 text-lg font-semibold">Advertising Profile</h2>

        {isLoading && (
          <p className="text-sm text-[var(--muted-foreground)]">Lade Profile...</p>
        )}

        {profiles && profiles.length === 0 && (
          <div className="flex items-center gap-2 text-sm text-[var(--muted-foreground)]">
            <AlertCircle className="h-4 w-4" />
            Noch kein Amazon-Account verknuepft. Klicke oben auf "Amazon verbinden".
          </div>
        )}

        {profiles && profiles.length > 0 && (
          <div className="space-y-3">
            {profiles.map((profile) => (
              <div
                key={profile.id}
                className="flex items-center justify-between rounded-md border border-[var(--border)] p-4"
              >
                <div>
                  <p className="text-sm font-medium">{profile.name}</p>
                  <p className="text-xs text-[var(--muted-foreground)]">
                    {profile.marketplace} &middot; {profile.type} &middot; ID: {profile.amazonProfileId}
                  </p>
                  {profile.tokenExpiresAt && (
                    <p className="text-xs text-[var(--muted-foreground)]">
                      Token laeuft ab:{' '}
                      {new Date(profile.tokenExpiresAt).toLocaleString('de-DE', {
                        timeZone: 'Europe/Berlin',
                      })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {profile.hasToken ? (
                    <span className="flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle2 className="h-3 w-3" /> Verbunden
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-red-500">
                      <XCircle className="h-3 w-3" /> Kein Token
                    </span>
                  )}
                  {profile.hasToken && (
                    <button
                      onClick={() => refreshMutation.mutate(profile.id)}
                      disabled={refreshMutation.isPending}
                      className="rounded-md border border-[var(--border)] p-1.5 hover:bg-[var(--accent)]"
                      title="Token erneuern"
                    >
                      <RefreshCw
                        className={`h-3.5 w-3.5 ${refreshMutation.isPending ? 'animate-spin' : ''}`}
                      />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Experiment-Mode */}
      <ExperimentModeSection />
    </div>
  )
}

// ============================================================
//  Experiment-Mode-Bereich (W7)
// ============================================================

const MODE_INFO: Record<string, { label: string; color: string; description: string }> = {
  shadow: { label: 'Shadow-Mode', color: 'bg-purple-100 text-purple-800 border-purple-200', description: 'System simuliert, ändert NICHTS' },
  approval: { label: 'Approval-Queue', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', description: 'Änderungen nur nach Genehmigung' },
  low_risk_auto: { label: 'Low-Risk-Auto', color: 'bg-green-100 text-green-800 border-green-200', description: 'Low-Risk-Experimente automatisch' },
  full_auto: { label: 'Full-Auto', color: 'bg-green-200 text-green-900 border-green-300', description: 'Low+Medium-Risk automatisch' },
}

function ExperimentModeSection() {
  const { data, isLoading } = useExperimentMode()
  const updateMut = useUpdateExperimentMode()

  if (isLoading || !data) return null

  const { config, levels } = data
  const modeInfo = MODE_INFO[config.mode] ?? { label: 'Shadow-Mode', color: 'bg-purple-100 text-purple-800 border-purple-200', description: 'System simuliert, ändert NICHTS' }

  function handleModeChange(mode: string) {
    if (mode === config.mode) return
    const info = MODE_INFO[mode]
    const confirmed = window.confirm(`Modus auf "${info?.label ?? mode}" wechseln?\n\n${info?.description ?? ''}`)
    if (confirmed) updateMut.mutate({ mode })
  }

  function handleToggle(field: string, currentValue: number) {
    updateMut.mutate({ [field]: currentValue === 1 ? false : true })
  }

  function handleNumberChange(field: string, value: number) {
    updateMut.mutate({ [field]: value })
  }

  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-6">
      <div className="flex items-center gap-2 mb-4">
        <FlaskConical className="h-5 w-5 text-purple-600" />
        <h2 className="text-lg font-semibold">Experiment-Engine</h2>
      </div>

      {/* Aktueller Modus */}
      <div className={`rounded-lg border p-4 mb-4 ${modeInfo.color}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold">{modeInfo.label}</p>
            <p className="text-sm">{modeInfo.description}</p>
          </div>
          <select
            value={config.mode}
            onChange={e => handleModeChange(e.target.value)}
            className="rounded border bg-white px-3 py-1.5 text-sm text-gray-900"
          >
            <option value="shadow">Shadow</option>
            <option value="approval">Approval</option>
            <option value="low_risk_auto" disabled={!levels.canUpgrade && config.mode !== 'low_risk_auto' && config.mode !== 'full_auto'}>Low-Risk-Auto</option>
            <option value="full_auto" disabled={!levels.canUpgrade && config.mode !== 'full_auto'}>Full-Auto</option>
          </select>
        </div>
      </div>

      {/* Toggles + Config */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="flex items-center justify-between rounded border p-3">
          <span>Loop aktiviert</span>
          <button
            onClick={() => handleToggle('loopEnabled', config.loopEnabled)}
            className={`rounded px-3 py-1 text-xs font-medium ${config.loopEnabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
          >
            {config.loopEnabled ? 'AN' : 'AUS'}
          </button>
        </div>
        <div className="flex items-center justify-between rounded border p-3">
          <span>Pause All</span>
          <button
            onClick={() => handleToggle('pauseAll', config.pauseAll)}
            className={`rounded px-3 py-1 text-xs font-medium ${config.pauseAll ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}
          >
            {config.pauseAll ? 'PAUSIERT' : 'AKTIV'}
          </button>
        </div>
        <div className="flex items-center justify-between rounded border p-3">
          <span>Max neue/Tag</span>
          <input
            type="number" min={1} max={20} value={config.maxNewPerDay}
            onChange={e => handleNumberChange('maxNewPerDay', parseInt(e.target.value) || 5)}
            className="w-16 rounded border px-2 py-1 text-right"
          />
        </div>
        <div className="flex items-center justify-between rounded border p-3">
          <span>Claude-Budget EUR/Monat</span>
          <input
            type="number" min={0} max={100} step={0.5} value={parseFloat(config.claudeBudgetEurMonth) || 15}
            onChange={e => handleNumberChange('claudeBudgetEurMonth', parseFloat(e.target.value) || 15)}
            className="w-20 rounded border px-2 py-1 text-right"
          />
        </div>
      </div>
    </div>
  )
}
